import { XrayRtcConnectionEvent, XrayRtcConnectionStatus } from '../types';

/**
 * Manages state for an X-Ray RTC connection.
 */
export class XrayRtcStateMachine {
  public constructor() {
    // create records for every supported state event
    for (const key in XrayRtcConnectionEvent) {
      const connectionEvent = XrayRtcConnectionEvent[key as keyof typeof XrayRtcConnectionEvent];
      const eventState = new XrayRtcEventState(new Event(connectionEvent));

      this.__events[connectionEvent] = eventState;

      // add listener for connection event that when emitted, simply
      // sets state and executes other event logic
      this.eventTarget.addEventListener(connectionEvent, () => {
        eventState.emitted = true;
        eventState.emittedOn = new Date();

        if (connectionEvent === XrayRtcConnectionEvent.clientDataChannelOpened) {
          // data channel is the event that marks the beginning of a connection
          this.connectionStart = Date.now();
        }

        if (connectionEvent === XrayRtcConnectionEvent.clientDataChannelClosed) {
          this.connectionEnd = Date.now();
        }

        if (isErrorEvent(connectionEvent)) {
          this.eventTarget.dispatchEvent(this.__events[XrayRtcConnectionEvent.connectionError].event);
        }
      });
    }
  }

  /**
   * Returns a snapshot of all connection events in their current state.
   */
  public get snapshot() {
    const serializedEvents = JSON.stringify(this.__events);
    const snapshotState = JSON.parse(serializedEvents) as Record<string, XrayRtcEventState>;
    return snapshotState;
  }

  /**
   * Returns connection status based on heuristic machine state.
   */
  public get status(): XrayRtcConnectionStatus {
    const connectionState = Object.keys(XrayRtcConnectionEvent).reduce<XrayRtcConnectionState>(
      (highestVote, key) => {
        const connectionEvent = XrayRtcConnectionEvent[key as keyof typeof XrayRtcConnectionEvent];
        const eventState = this.__events[connectionEvent];

        return Math.max(eventState.emitted ? eventState.connectionVote : -1, highestVote);
      },
      XrayRtcConnectionState.Idle
    );

    switch (connectionState) {
      default:
      case XrayRtcConnectionState.Idle:
        return 'idle';
      case XrayRtcConnectionState.Closed:
        return 'closed';
      case XrayRtcConnectionState.Closing:
        return 'closing';
      case XrayRtcConnectionState.Error:
        return 'error';
      case XrayRtcConnectionState.Pending:
        return 'pending';
      case XrayRtcConnectionState.Running:
        return 'running';
    }
  }

  /**
   *  Emits connection event to set espective machine state.
   */
  public emit(evt: XrayRtcConnectionEvent) {
    console.log(evt);
    if (
      evt === XrayRtcConnectionEvent.connectionError ||
      evt === XrayRtcConnectionEvent.connectionNotSupportedError
    ) {
      // do not allow connection error event to be emitted (is handled heuristically elsewhere)
      return;
    }

    this.eventTarget.dispatchEvent(this.__events[evt].event);
  }

  /**
   * Subscribes to a connection event.
   */
  public on(evt: XrayRtcConnectionEvent, handler: () => void) {
    this.eventTarget.addEventListener(evt, handler);
  }

  /**
   * Resets event states to initial machine state.
   */
  public reset() {
    for (const key in XrayRtcConnectionEvent) {
      const connectionEvent = XrayRtcConnectionEvent[key as keyof typeof XrayRtcConnectionEvent];
      const connectionState = this.__events[connectionEvent];

      connectionState.emitted = false;
      connectionState.emittedOn = null;
    }

    this.connectionStart = 0;
    this.connectionEnd = 0;

    // reset connection to instantiated state
    this.emit(XrayRtcConnectionEvent.connectionInstantiated);
  }

  /**
   * Returns the number of seconds since the RTC data channel was opened.
   */
  public get dataChannelDurationSeconds() {
    return this.connectionStart
      ? Math.round(((this.connectionEnd || Date.now()) - this.connectionStart) / 1000)
      : 0;
  }

  public signalingState: RTCSignalingState = 'stable';

  private readonly __events: Record<string, XrayRtcEventState> = {};
  private readonly eventTarget: EventTarget = new EventTarget();
  private connectionStart = 0;
  private connectionEnd = 0;
}

//
//
// local types
//
//

enum XrayRtcConnectionState {
  Idle = 1,
  Pending = 2,
  Running = 3,
  Closing = 4,
  Closed = 5,
  Error = 6,
}

class XrayRtcEventState {
  public readonly event: Event;
  public readonly connectionVote: XrayRtcConnectionState;
  public emitted = false;
  public emittedOn: Date | null = null;

  public constructor(event: Event) {
    this.event = event;

    if (isErrorEvent(event.type)) {
      this.connectionVote = XrayRtcConnectionState.Error;
      return;
    }

    switch (event.type) {
      case XrayRtcConnectionEvent.connectionInstantiated:
        this.connectionVote = XrayRtcConnectionState.Idle;
        break;
      case XrayRtcConnectionEvent.connectionStop:
        this.connectionVote = XrayRtcConnectionState.Closing;
        break;
      case XrayRtcConnectionEvent.peerConnectionClosed:
      case XrayRtcConnectionEvent.clientDataChannelClosed:
        this.connectionVote = XrayRtcConnectionState.Closed;
        break;
      case XrayRtcConnectionEvent.clientDataChannelOpened:
        this.connectionVote = XrayRtcConnectionState.Running;
        break;
      default:
        this.connectionVote = XrayRtcConnectionState.Pending;
        break;
    }
  }
}

//
//
// helpers
//
//

function isErrorEvent(evt: string) {
  // IMPORTANT:
  // The following events are NOT considered error events here
  // because they are eirther directly handled or are not fatal;
  // therefore, do NOT include any in switch-case below:
  // - clientIceCandidateGatheringError
  // - connectionError
  // - connectionNotSupportedError
  switch (evt) {
    case XrayRtcConnectionEvent.clientDataChannelError:
    case XrayRtcConnectionEvent.deviceDataChannelError:
    case XrayRtcConnectionEvent.deviceIceCandidateAddError:
    case XrayRtcConnectionEvent.remoteDescriptionAnswerError:
    case XrayRtcConnectionEvent.localDescriptionError:
    case XrayRtcConnectionEvent.offerCreateError:
    case XrayRtcConnectionEvent.peerConnectionError:
    case XrayRtcConnectionEvent.remoteDescriptionError:
    case XrayRtcConnectionEvent.reporterError:
    case XrayRtcConnectionEvent.webSocketClosedAbnormal:
    case XrayRtcConnectionEvent.webSocketError:
    case XrayRtcConnectionEvent.webSocketTimeout:
      return true;
    default:
      return false;
  }
}
